import { devLog } from '@/lib/logger';
import { toast } from '@/lib/sonner-adapter';
import { supabaseBrowser } from '@/lib/supabase-browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export interface BridgeWallet {
  id: string;
  bridge_wallet_id: string;
  chain: string;
  address: string;
  /** Liquidation Address for tracked deposits (PRD-31) */
  deposit_address?: string;
  deposit_address_note?: string;
  balances: {
    currency: string;
    balance: string; // string decimal
    available: string;
    usd_value?: string;
  }[];
}

export interface DepositAddress {
  id: string;
  /** The address to show to user for deposits (LA address preferred) */
  address: string;
  chain: string;
  currency: string;
  external_id: string;
  /** Type of address: 'liquidation_address' = tracked, 'wallet' = untracked fallback */
  address_type: 'liquidation_address' | 'wallet';
  /** Original wallet address (for reference) */
  wallet_address?: string;
}

export interface VirtualAccount {
  va_id: string;
  deposit_instructions: {
    rails?: string[];
    // Bank details
    bank_name?: string;
    bank_address?: string;
    // Beneficiary (CRITICAL)
    beneficiary_name?: string;
    beneficiary_address?: string;
    // SEPA (EUR)
    iban?: string;
    bic?: string;
    // ACH (USD)
    routing?: string;
    account?: string;
    wire_routing?: string;
    account_type?: string;
    // SPEI (MXN)
    clabe?: string;
    // PIX (BRL)
    pix_key?: string;
    pix_key_type?: string;
    // Reference
    reference?: string;
    memo?: string;
    // Legacy alias
    swift?: string;
  };
}

export interface TransferParams {
  from_wallet_id: string;
  to_wallet_id?: string; // Internal
  destination_address?: string; // External
  destination_chain?: string; // External
  amount: string;
  currency: string;
  memo?: string;
}

export interface FiatTransferParams {
  from_wallet_id: string;
  to_external_account_id: string;
  amount: string;
  currency: 'USD' | 'EUR';
  payment_rail: 'ach' | 'wire' | 'sepa';
  source_currency?: string; // Optional: Source stablecoin (USDC, USDT, etc.) - auto-detected if not provided
  reference?: string; // Optional: Reference/memo that recipient will see in bank statement
}

export interface UserTransaction {
  id: string;
  type: string;
  status: string;
  amount: {
    currency: string;
    value: string;
  };
  usd_value?: string;
  source?: any;
  destination?: any;
  fees?: any;
  receipt?: any;
  created_at: string;
  completed_at?: string;
}

export interface ExternalAccount {
  id: string;
  type: string;
  bank_name?: string;
  account_last4?: string;
  account_number?: string; // Full account number (for user's own accounts - no masking)
  routing_number?: string;
  iban?: string; // Full IBAN for SEPA accounts
  swift_bic?: string; // SWIFT/BIC code for SEPA accounts
  clabe?: string; // CLABE for Mexican SPEI accounts
  status: string;
  currency?: string;
  account_type?: string; // 'us' for US accounts, 'iban' for SEPA accounts, 'spei' for Mexican accounts
  account_owner_name?: string;
  country?: string;
}

// Fetch wallets
async function fetchWallets() {
  const { data, error } = await supabaseBrowser.functions.invoke('wallet', {
    body: { action: 'user.wallets' }
  });
  if (error) {
    console.error('[useWalletBalances] Edge Function error:', error);
    throw error;
  }
  
  // Edge Function returns { wallets: [...] }
  const wallets = data?.wallets;
  
  devLog('[useWalletBalances] Raw Edge Function response:', {
    hasData: !!data,
    dataKeys: data ? Object.keys(data) : [],
    hasWallets: !!wallets,
    walletsType: typeof wallets,
    walletsIsArray: Array.isArray(wallets),
    walletsLength: Array.isArray(wallets) ? wallets.length : 'N/A'
  });
  
  if (!wallets) {
    console.warn('[useWalletBalances] No wallets in response:', data);
    return [];
  }
  
  if (!Array.isArray(wallets)) {
    console.error('[useWalletBalances] Wallets is not an array:', typeof wallets, wallets);
    return [];
  }
  
  // Log wallet structure for debugging
  if (wallets.length > 0) {
    devLog('[useWalletBalances] First wallet structure:', {
      id: wallets[0].id,
      chain: wallets[0].chain,
      hasBalances: !!wallets[0].balances,
      balancesType: typeof wallets[0].balances,
      balancesIsArray: Array.isArray(wallets[0].balances),
      balancesLength: Array.isArray(wallets[0].balances) ? wallets[0].balances.length : 'N/A',
      balancesPreview: Array.isArray(wallets[0].balances) ? wallets[0].balances.slice(0, 2) : wallets[0].balances
    });
  }
  
  devLog('[useWalletBalances] Fetched wallets:', wallets.length, 'wallets');
  return wallets as BridgeWallet[];
}

export function useWalletBalances() {
  return useQuery({
    queryKey: ['wallet-balances'],
    queryFn: fetchWallets,
    refetchInterval: 30000, // Refresh every 30s
    refetchOnMount: true, // Always refetch on mount to fix UI update bug
    refetchOnWindowFocus: true, // Refetch when window regains focus
    staleTime: 0, // Always consider data stale to ensure fresh data
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

/**
 * Create/Get Deposit Address (Crypto)
 * 
 * PRD-31: Returns Liquidation Address for tracked deposits.
 * The LA is automatically created when wallet is provisioned.
 * Falls back to wallet address if no LA exists (with warning).
 */
export function useDepositAddress() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { chain: string; currency?: string }): Promise<DepositAddress> => {
      const { chain, currency = 'USDC' } = params;

      // 1. Get User Wallets
      let wallets = queryClient.getQueryData<BridgeWallet[]>(['wallet-balances']);
      if (!wallets || wallets.length === 0) {
        wallets = await fetchWallets();
      }

      // 2. Find wallet for chain (case insensitive)
      let wallet = wallets?.find(w => w.chain.toLowerCase() === chain.toLowerCase());

      // 3. Create wallet if missing (this also creates LA via PRD-31)
      if (!wallet) {
        const { data, error: createError } = await supabaseBrowser.functions.invoke('wallet', {
          body: { action: 'user.create', chain }
        });

        if (createError) throw new Error(createError.message || 'Failed to create wallet');

        // PRD-31: New wallet creation returns deposit_address
        if (data?.wallet?.deposit_address) {
          await queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
          return {
            id: data.liquidation_address?.id || data.wallet.id,
            address: data.wallet.deposit_address, // ✅ Use LA address
            chain: data.wallet.chain,
            currency: currency,
            external_id: data.liquidation_address?.id || data.wallet.id,
            address_type: 'liquidation_address',
            wallet_address: data.wallet.address
          };
        }

        // Refetch to get updated wallet
        await queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
        wallet = data?.wallet;
      }

      if (!wallet) throw new Error('Could not find or create wallet');
      if (!wallet.address) throw new Error('Wallet address not available. Please try again.');

      // 4. PRD-31: Check if wallet has deposit_address (LA)
      if (wallet.deposit_address) {
        return {
          id: wallet.id,
          address: wallet.deposit_address, // ✅ Use LA address
          chain: wallet.chain,
          currency: currency,
          external_id: wallet.bridge_wallet_id || wallet.id,
          address_type: 'liquidation_address',
          wallet_address: wallet.address
        };
      }

      // 5. Fallback: Query liquidation_addresses table directly
      try {
        const { data: laData } = await supabaseBrowser
          .from('liquidation_addresses')
          .select('id, address, chain, asset, currency')
          .eq('wallet_id', wallet.id)
          .eq('destination_type', 'wallet')
          .eq('status', 'active')
          .maybeSingle();

        if (laData?.address) {
          return {
            id: laData.id,
            address: laData.address, // ✅ Use LA address
            chain: laData.chain,
            currency: laData.currency || currency,
            external_id: laData.id,
            address_type: 'liquidation_address',
            wallet_address: wallet.address
          };
        }
      } catch (err) {
        console.warn('[useDepositAddress] Failed to query LA table:', err);
      }

      // 6. Last resort: Return wallet address with warning
      console.warn('[useDepositAddress] No LA found for wallet, using wallet address (deposits may not be tracked)');
      return {
        id: wallet.bridge_wallet_id || wallet.id,
        address: wallet.address, // ⚠️ Fallback to wallet address
        chain: wallet.chain || chain,
        currency: currency,
        external_id: wallet.bridge_wallet_id || wallet.id,
        address_type: 'wallet', // Mark as wallet, not LA
        wallet_address: wallet.address
      };
    }
  });
}

// Create Virtual Account (Fiat)
export function useVirtualAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { currency: 'USD' | 'EUR' }) => {
      const { currency } = params;
      const chain = 'ethereum'; // Default for receiving USDC
      const destCurrency = 'USDC';

      // 1. Get Wallet for receiving funds
      let wallets = queryClient.getQueryData<BridgeWallet[]>(['wallet-balances']);
      if (!wallets) wallets = await fetchWallets();
      let wallet = wallets?.find(w => w.chain.toLowerCase() === chain.toLowerCase());

      if (!wallet) {
        // PRD-31: Use wallet hub endpoint that auto-creates Liquidation Address
        const { data, error: createError } = await supabaseBrowser.functions.invoke('wallet', {
          body: { action: 'user.create', chain }
        });
        const newWallet = data?.wallet;
        if (createError) throw createError;
        wallet = newWallet;
      }

      if (!wallet) throw new Error('No wallet available to receive funds');

      // 2. Create VA with bridge_wallet_id (according to Bridge API on-ramp guide)
      // Bridge API requires: destination.bridge_wallet_id for automatic delivery to wallet
      const bridgeWalletId = wallet.bridge_wallet_id || wallet.id;
      if (!bridgeWalletId) {
        throw new Error('Bridge wallet ID not available. Please try again.');
      }

      const response = await supabaseBrowser.functions.invoke('virtual-accounts-create', {
        body: {
          source_currency: currency,
          destination: {
            type: 'wallet', // Use wallet type to specify bridge_wallet_id
            wallet_id: bridgeWalletId, // Bridge wallet ID (wal_xxx)
            chain: chain,
            currency: destCurrency
          }
        }
      });

      // Handle response - Edge Function returns { va_id, deposit_instructions } directly
      // or { data, error } format from Supabase client
      if (!response) {
        throw new Error('No response from virtual account creation');
      }

      // Check for error in response
      if (response.error) {
        throw new Error(response.error.message || response.error || 'Failed to create virtual account');
      }

      // Edge Function returns { va_id, deposit_instructions } directly
      // Supabase client may wrap it in { data } or return it directly
      const vaData = response.data || response;

      if (!vaData || (!vaData.va_id && !vaData.deposit_instructions)) {
        throw new Error('Invalid response format from virtual account creation');
      }

      return vaData as VirtualAccount;
    }
  });
}

// Transfer Funds (Crypto)
// Uses dedicated wallet-transfer function to bypass bridge hub routing issues
export function useWalletTransfer() {
  return useMutation({
    mutationFn: async (params: TransferParams) => {
      // Validate required fields before API call
      if (!params.from_wallet_id) {
        throw new Error('Source wallet is required');
      }
      if (!params.amount || parseFloat(params.amount) <= 0) {
        throw new Error('Valid amount is required');
      }
      if (!params.currency) {
        throw new Error('Currency is required');
      }

      // For external transfer, need destination_address AND destination_chain
      if (!params.to_wallet_id) {
        if (!params.destination_address?.trim()) {
          throw new Error('Destination address is required for external transfer');
        }
        if (!params.destination_chain) {
          throw new Error('Network/chain is required for external transfer');
        }
      }

      devLog('[useWalletTransfer] Sending transfer:', {
        from_wallet_id: params.from_wallet_id,
        to_wallet_id: params.to_wallet_id,
        destination_address: params.destination_address ? `${params.destination_address.slice(0, 10)}...` : undefined,
        destination_chain: params.destination_chain,
        amount: params.amount,
        currency: params.currency
      });

      // Deterministic idempotency key prevents double transactions
      // 60-second bucket allows legitimate repeat transfers
      const timeBucket = Math.floor(Date.now() / 60000);
      const dest = params.destination_address || params.to_wallet_id || 'internal';
      const idemKey = `transfer:${params.from_wallet_id}:${dest}:${params.amount}:${params.currency}:${timeBucket}`;

      const res = await supabaseBrowser.functions.invoke('wallet-transfer', {
        body: {
          ...params,
          idempotency_key: idemKey
        }
      });

      // Parse error response - could be in res.error or res.data
      const errorData = res.error?.context || res.data;

      if (res.error || (errorData && errorData.ok === false)) {
        const errorCode = errorData?.code;
        const errorMessage = errorData?.error;

        // Map error codes to user-friendly messages
        const userMessages: Record<string, string> = {
          'insufficient_balance': 'Insufficient funds. Please check your balance.',
          'invalid_amount': 'Please enter a valid amount.',
          'invalid_address': 'Invalid destination address.',
          'validation_error': errorMessage || 'Invalid request. Please check your details.',
          'wallet_not_found': 'Wallet not found.',
          'transfer_failed': errorMessage || 'Transfer failed. Please try again.'
        };

        const friendlyMessage = userMessages[errorCode] || errorMessage || res.error?.message || 'Transfer failed';
        throw new Error(friendlyMessage);
      }

      return res.data;
    },
    onSuccess: () => {
      toast.success('Transfer initiated successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Transfer failed');
    }
  });
}

// Transfer Fiat (Withdrawal)
// Bridge API: Converts stablecoin in wallet → fiat to bank account
// Uses dedicated Edge Function to avoid hub routing issues
export function useFiatWithdrawal() {
  return useMutation({
    mutationFn: async (params: FiatTransferParams) => {
      // Deterministic idempotency key prevents double withdrawals
      // 60-second bucket allows legitimate repeat withdrawals
      const timeBucket = Math.floor(Date.now() / 60000);
      const idemKey = `fiat:${params.from_wallet_id}:${params.to_external_account_id}:${params.amount}:${params.currency}:${timeBucket}`;

      // Route through Bridge Hub (RPC-only canon pattern)
      const { data: { session } } = await supabaseBrowser.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const url = `${supabaseUrl}/functions/v1/bridge?action=wallet.transferFiat&v=1`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'Idempotency-Key': idemKey
        },
        body: JSON.stringify({
          from_wallet_id: params.from_wallet_id,
          to_external_account_id: params.to_external_account_id,
          amount: params.amount,
          currency: params.currency.toLowerCase(), // Target fiat currency (usd, eur)
          source_currency: params.source_currency?.toLowerCase(), // Source stablecoin (optional, auto-detected)
          payment_rail: params.payment_rail,
          // Optional reference - recipient will see this in their bank statement
          ...(params.reference && { reference: params.reference })
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorCode = errorData?.code;
        const errorMessage = errorData?.error || errorData?.message;

        // Map error codes to user-friendly messages
        const userMessages: Record<string, string> = {
          'insufficient_balance': 'Insufficient funds. Please check your balance.',
          'invalid_amount': 'Please enter a valid amount.',
          'validation_error': errorMessage || 'Invalid request. Please check your details.',
          'account_inactive': 'Selected bank account is not active.',
          'currency_mismatch': errorMessage || 'Currency mismatch with selected account.',
          'external_account_not_found': 'Bank account not found.',
          'wallet_not_found': 'Wallet not found.',
          'bridge_error': errorMessage || 'Transfer failed. Please try again.'
        };

        const friendlyMessage = userMessages[errorCode] || errorMessage || 'Withdrawal failed';
        throw new Error(friendlyMessage);
      }

      const data = await response.json();
      return data;
    },
    onSuccess: () => {
      toast.success('Withdrawal initiated successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Withdrawal failed');
    }
  });
}

// Fetch Transactions
export function useWalletTransactions(filters?: any) {
  return useQuery({
    queryKey: ['wallet-transactions', filters],
    queryFn: async () => {
      const { data, error } = await supabaseBrowser.functions.invoke('user-transactions-search', {
        method: 'POST',
        body: {
          limit: 50,
          ...filters
        }
      });

      if (error) throw error;
      return data.items as UserTransaction[];
    }
  });
}

// Fetch External Accounts
export function useExternalAccounts(options?: { enabled?: boolean; retry?: number; onError?: (error: Error) => void }) {
  return useQuery({
    queryKey: ['external-accounts'],
    queryFn: async () => {
      const { data, error } = await supabaseBrowser.functions.invoke('wallet', {
        body: { action: 'user.externalAccounts' }
      });
      if (error) {
        // Handle specific error cases
        if (error.message?.includes('404') || error.message?.includes('not found')) {
          // Customer not found or no accounts - return empty array
          return [] as ExternalAccount[];
        }
        throw error;
      }
      return (data?.accounts || []) as ExternalAccount[];
    },
    enabled: options?.enabled !== false,
    retry: options?.retry ?? 1,
    onError: options?.onError,
  });
}

// Realtime Subscription Hook
export function useWalletRealtime() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = supabaseBrowser
      .channel('wallet-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `user_id=eq.${supabaseBrowser.auth.getUser().then(({ data }) => data.user?.id)}` // Filter by user if possible, otherwise fetch user ID first
        },
        () => {
          toast.info('Wallet updated');
          queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
          queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'wallet_transactions'
        },
        () => {
          // Also listen to wallet_transactions if that's where bridge transfers go
          queryClient.invalidateQueries({ queryKey: ['wallet-balances'] });
          queryClient.invalidateQueries({ queryKey: ['wallet-transactions'] });
        }
      )
      .subscribe();

    return () => {
      supabaseBrowser.removeChannel(channel);
    };
  }, [queryClient]);
}
